import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../../utils/apolloClient';
//import { integrationData } from './mocks/integrationData';
import config from '../../../utils/config';

const initialState = {
  subscriptionsLoading: false,
  connectionsLoading: false,
  accessGrantsLoading: false,
  dealerIntegrationLoading: false,
  hasLoadingError: false,
  subscriptionsByEntityId: {},
  marketplaceListedApps: {},
  notInMarketplaceApps: {},
  subscriptionIdCMFMap: {},
  connectionsByEntityId: {},
  accessGrants: [],
  apiIdToNameMapping: {},
  pendingRequests: 0,
};

export const getSubscriptions = createAsyncThunk(
  'getIntegrationsData',
  async ({entityIds, token}) => {
    let response = await client.authorizeRequest({
      method: 'GET',
      url: `${config.serviceEndpoints.mpDpMetadataUrl}/dealer-integrations/subscriptions`,
      params: {
        entityIds: entityIds?.toString()
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  }
);

export const getConnectionsForAllEntityIds = createAsyncThunk(
  'getConnectionsForAllEntityIds',
  async ({entityIds, token}) => {
    let response = await client.authorizeRequest({
      method: 'POST',
      url: `${config.serviceEndpoints.mpDpMetadataUrl}/v1/dealer-integrations`,
      data: {
        dealerOrgIds : entityIds
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  }
);

export const getConnections = createAsyncThunk(
  'getConnections',
  async ({entityIds, token}) => {
    let response = await client.authorizeRequest({
      method: 'GET',
      url: `${config.serviceEndpoints.mpDpMetadataUrl}/dealer-integrations/connections`,
      params: {
        entityIds: entityIds?.toString()
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  }
);

export const getAccessGrants = createAsyncThunk(
  'getAccessGrants',
  async ({entityIds, token}) => {
    let response = await client.authorizeRequest({
      method: 'GET',
      url: `${config.serviceEndpoints.mpDpMetadataUrl}/dealer-integrations/access-grants`,
      params: {
        integrationDetails: true,
        entityIds: entityIds?.toString()
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  }
);

export const dealerIntegrationsReport = createSlice({
  name: 'dealerIntegrationsReport',
  initialState,
  reducers: {
    reset: () => initialState
  },
  extraReducers: {
    [getSubscriptions.fulfilled]: (state, action) => {
      state.pendingRequests = state.pendingRequests - 1;
      let subscriptions = state.subscriptionsByEntityId;
      action.payload.subscriptions.forEach(sub => {
        subscriptions = {
          ...subscriptions,
          [sub?.entityId]: sub?.subscriptions
        }
      });
    state.subscriptionsByEntityId = subscriptions;
    state.marketplaceListedApps = {
      ...state.marketplaceListedApps,
      ...action.payload.marketplaceListedApps
    };
    state.notInMarketplaceApps = {
      ...state.notInMarketplaceApps,
      ...action.payload.notInMarketplaceApps
    };
    state.subscriptionIdCMFMap = {
      ...state.subscriptionIdCMFMap,
      ...action.payload.subscriptionIdCMFMap
    };
    state.subscriptionsLoading = false;
    state.hasLoadingError = false;
    },
    [getSubscriptions.pending]: state => {
      state.pendingRequests = ++state.pendingRequests;
      state.subscriptionsLoading = true;
      state.hasLoadingError = false;
    },
    [getSubscriptions.rejected]: state => {
      state.pendingRequests = --state.pendingRequests;
      state.subscriptionsLoading = false;
      state.hasLoadingError = true;
    },
    [getConnectionsForAllEntityIds.fulfilled]: (state, action) => {
      state.pendingRequests = state.pendingRequests - 1;
      const {
        apiIdToNameMap,
        marketplaceListedApps,
        notInMarketplaceApps,
        subscriptionIdCMFMap,
        accessGrants
      } = action.payload;
      let subscriptions = state.subscriptionsByEntityId;
      action.payload.subscriptions.forEach(sub => {
        subscriptions[sub?.entityId] = sub?.subscriptions
      });
      let connections = state.connectionsByEntityId;
      action.payload.connections.forEach(con => {
        connections[con?.entityId] = con?.connections
      });
      state.subscriptionsByEntityId = subscriptions;
      state.connectionsByEntityId = connections;
      state.marketplaceListedApps = {
        ...state.marketplaceListedApps,
        ...marketplaceListedApps
      };
      state.notInMarketplaceApps = {
        ...state.notInMarketplaceApps,
        ...notInMarketplaceApps
      };
      state.subscriptionIdCMFMap = {
        ...state.subscriptionIdCMFMap,
        ...subscriptionIdCMFMap
      };
      state.apiIdToNameMapping = {
        ...state.apiIdToNameMapping,
        ...apiIdToNameMap
      };
      state.accessGrants = [
        ...state.accessGrants, ...accessGrants
      ];

      state.dealerIntegrationLoading = false;
      state.hasLoadingError = false;
    },
    [getConnectionsForAllEntityIds.pending]: state => {
      state.pendingRequests = ++state.pendingRequests;
      state.dealerIntegrationLoading = true;
      state.hasLoadingError = false;
    },
    [getConnectionsForAllEntityIds.rejected]: state => {
      state.pendingRequests = --state.pendingRequests;
      state.dealerIntegrationLoading = false;
      state.hasLoadingError = true;
    },
    [getConnections.fulfilled]: (state, action) => {
      state.pendingRequests = --state.pendingRequests;
      let connections = state.connectionsByEntityId;
      action.payload.connections.forEach(con => {
        connections = {
          ...connections,
          [con?.entityId]: con?.connections
        }
      });
      state.connectionsByEntityId = connections;
      state.apiIdToNameMapping = {
        ...state.apiIdToNameMapping,
        ...action.payload.apiIdToNameMap
      };
      state.connectionsLoading = false;
      state.hasLoadingError = false;
    },
    [getConnections.pending]: state => {
      state.pendingRequests = ++state.pendingRequests;
      state.connectionsLoading = true;
      state.hasLoadingError = false;
    },
    [getConnections.rejected]: state => {
      state.pendingRequests = --state.pendingRequests;
      state.connectionsLoading = false;
      state.hasLoadingError = true;
    },
    [getAccessGrants.fulfilled]: (state, action) => {
      state.pendingRequests = --state.pendingRequests;
      state.accessGrants.push(...action.payload.accessGrants);
      state.accessGrantsLoading = false;
      state.hasLoadingError = false;
    },
    [getAccessGrants.pending]: state => {
      state.pendingRequests = ++state.pendingRequests;
      state.accessGrantsLoading = true;
      state.hasLoadingError = false;
    },
    [getAccessGrants.rejected]: state => {
      state.pendingRequests = --state.pendingRequests;
      state.accessGrantsLoading = false;
      state.hasLoadingError = true;
    }
  }
});

export const {
  reset
} = dealerIntegrationsReport.actions;

export default dealerIntegrationsReport.reducer;